import { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import type { EntitlementDiagnosticsResult, EntitlementResolution } from '../types';

/** Phase 5, observe-only entitlement diagnostics — the client-side half of
 *  the one canonical resolution mechanism (see functions/src/
 *  entitlementResolution.ts). Calls the getEntitlementDiagnostics
 *  callable rather than reading tenants/{tenantId}/entitlements directly:
 *  Compass's own client has no Firestore rule granting it read access to
 *  that collection (only Compass Manage's isActiveProductAdmin() does —
 *  see the Phase 5 architecture note), so a callable is both the only
 *  option today and, independently, the right one — it keeps resolution
 *  server-authoritative instead of duplicating the logic client-side.
 *
 *  Diagnostic only: nothing reads this hook's result to hide UI, block a
 *  route, or change behavior. */
export function useEntitlementDiagnostics(): {
  tenantId: string | null;
  resolvedAt: string | null;
  modules: EntitlementResolution[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
} {
  const [result, setResult] = useState<EntitlementDiagnosticsResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const call = httpsCallable<Record<string, never>, EntitlementDiagnosticsResult>(functions, 'getEntitlementDiagnostics');
    call({})
      .then((res) => {
        if (cancelled) return;
        setResult(res.data);
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Entitlement diagnostics could not be loaded.');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tick]);

  return {
    tenantId: result?.tenantId ?? null,
    resolvedAt: result?.resolvedAt ?? null,
    modules: result?.modules ?? [],
    loading,
    error,
    refresh,
  };
}

/** "Is Training active?" style convenience — a lookup over an
 *  already-resolved list, not a second resolution path. */
export function findModuleResolution(modules: EntitlementResolution[], moduleKey: string): EntitlementResolution | null {
  return modules.find((m) => m.moduleKey === moduleKey) ?? null;
}
